import type {
  Action,
  ActionValidation,
  GameState,
  MapDefinition,
  Player,
  PlayerId,
  TerritoryId,
} from './types.js';

/** Look up a player by id. Returns undefined for unknown ids. */
function playerById(state: GameState, id: PlayerId): Player | undefined {
  return state.players.find((p) => p.id === id);
}

/** True iff `a` and `b` share a team. Same player counts as same team. */
export function sameTeam(state: GameState, a: PlayerId, b: PlayerId): boolean {
  if (a === b) return true;
  const ap = playerById(state, a);
  const bp = playerById(state, b);
  if (!ap || !bp) return false;
  return ap.team === bp.team;
}

const ok: ActionValidation = { ok: true };
const fail = (reason: string): ActionValidation => ({ ok: false, reason });

export function validateAction(
  state: GameState,
  action: Action,
  map: MapDefinition
): ActionValidation {
  if (state.status.kind === 'victory') {
    return fail('Game is over');
  }
  if (action.playerId !== state.turn.currentPlayer) {
    return fail('Not your turn');
  }
  const player = state.players.find((p) => p.id === action.playerId);
  if (!player) return fail('Unknown player');
  if (player.eliminated) return fail('Eliminated player cannot act');

  // While awaiting an advance, only an 'advance' action is legal —
  // EXCEPT autoSkip, which handles awaitingAdvance internally
  // (auto-advance with maxUnits before passing turn).
  if (
    state.turn.phase === 'attack' &&
    state.turn.attack.kind === 'awaitingAdvance' &&
    action.type !== 'advance' &&
    action.type !== 'autoSkip'
  ) {
    return fail('Must resolve advance before any other action');
  }

  switch (action.type) {
    case 'deploy':
      return validateDeploy(state, action);
    case 'attack':
      return validateAttack(state, action, map);
    case 'advance':
      return validateAdvance(state, action);
    case 'fortify':
      return validateFortify(state, action, map);
    case 'endPhase':
      return validateEndPhase(state);
    case 'endTurn':
      return validateEndTurn(state);
    case 'undoLastDeploy':
      return validateUndoLastDeploy(state);
    case 'autoSkip':
      return validateAutoSkip(state, action);
  }
}

/** Server-only action — generated by the 24hr cron sweep. The
 *  top-level guards above already cover "game over" and "not your
 *  turn"; the only additional check is the turnNumber race guard:
 *  if the cron's read-then-act window straddles a real user action,
 *  the turnNumber won't match and we reject so the user's action
 *  wins. */
function validateAutoSkip(
  state: GameState,
  action: Extract<Action, { type: 'autoSkip' }>
): ActionValidation {
  if (action.turnNumber !== state.turnNumber) {
    return fail(
      `autoSkip turnNumber mismatch (action=${action.turnNumber}, state=${state.turnNumber})`
    );
  }
  return ok;
}

function validateUndoLastDeploy(state: GameState): ActionValidation {
  if (state.turn.phase !== 'deploy') {
    return fail('Can only undo during deploy phase');
  }
  if (state.turn.deploysThisTurn.length === 0) {
    return fail('No deploys to undo');
  }
  return ok;
}

function validateDeploy(
  state: GameState,
  action: Extract<Action, { type: 'deploy' }>
): ActionValidation {
  if (state.turn.phase !== 'deploy') return fail('Not in deploy phase');
  if (action.units <= 0) return fail('Must deploy at least 1 unit');
  if (action.units > state.turn.reinforcementsRemaining) {
    return fail(
      `Only ${state.turn.reinforcementsRemaining} reinforcement(s) remain`
    );
  }
  const t = state.territories[action.territory];
  if (!t) return fail('Territory does not exist');
  if (t.owner !== action.playerId) return fail('You do not own that territory');
  return ok;
}

function validateAttack(
  state: GameState,
  action: Extract<Action, { type: 'attack' }>,
  map: MapDefinition
): ActionValidation {
  if (state.turn.phase !== 'attack') return fail('Not in attack phase');
  if (state.turn.attack.kind !== 'free') {
    return fail('Cannot attack while awaiting advance');
  }
  if (action.attackingDice < 1 || action.attackingDice > 3) {
    return fail('attackingDice must be 1, 2, or 3');
  }
  if (action.from === action.to) {
    return fail('Cannot attack the same territory');
  }
  const from = state.territories[action.from];
  const to = state.territories[action.to];
  if (!from) return fail('From territory does not exist');
  if (!to) return fail('To territory does not exist');
  if (from.owner !== action.playerId) {
    return fail('You do not own the attacking territory');
  }
  if (sameTeam(state, to.owner, action.playerId)) {
    // Covers both self-attack (own tile) and friendly fire (teammate's
    // tile) — the v0.7 team rule blocks both with one check.
    if (to.owner === action.playerId) {
      return fail('Cannot attack your own territory');
    }
    return fail('Cannot attack a teammate');
  }
  if (from.units < 2) {
    return fail('Need at least 2 units to attack');
  }
  if (action.attackingDice > from.units - 1) {
    return fail(
      `Cannot use ${action.attackingDice} dice with only ${from.units} unit(s)`
    );
  }
  const fromTerr = map.territories.find((t) => t.id === action.from);
  if (!fromTerr) return fail('From territory not on map');
  if (!fromTerr.adjacent.includes(action.to)) {
    return fail('Territories are not adjacent');
  }
  return ok;
}

function validateAdvance(
  state: GameState,
  action: Extract<Action, { type: 'advance' }>
): ActionValidation {
  if (state.turn.phase !== 'attack') return fail('Not in attack phase');
  if (state.turn.attack.kind !== 'awaitingAdvance') {
    return fail('No advance pending');
  }
  const sub = state.turn.attack;
  if (action.from !== sub.from || action.to !== sub.to) {
    return fail('Advance must use the conquering territory pair');
  }
  if (action.units < sub.minUnits || action.units > sub.maxUnits) {
    return fail(
      `Advance must move between ${sub.minUnits} and ${sub.maxUnits} unit(s)`
    );
  }
  return ok;
}

function validateFortify(
  state: GameState,
  action: Extract<Action, { type: 'fortify' }>,
  map: MapDefinition
): ActionValidation {
  if (state.turn.phase !== 'fortify') return fail('Not in fortify phase');
  if (state.turn.fortifyUsed) return fail('Already fortified this turn');
  if (action.units <= 0) return fail('Must move at least 1 unit');
  if (action.from === action.to) return fail('From and to must differ');
  const from = state.territories[action.from];
  const to = state.territories[action.to];
  if (!from) return fail('From territory does not exist');
  if (!to) return fail('To territory does not exist');
  if (from.owner !== action.playerId) {
    return fail('You do not own the source territory');
  }
  if (to.owner !== action.playerId) {
    return fail('You do not own the destination territory');
  }
  if (action.units > from.units - 1) {
    return fail('Must leave at least 1 unit behind');
  }
  if (!isConnectedViaTeam(state, map, action.from, action.to, action.playerId)) {
    return fail(
      "Territories not connected through your team's territories"
    );
  }
  return ok;
}

function validateEndPhase(state: GameState): ActionValidation {
  switch (state.turn.phase) {
    case 'deploy':
      if (state.turn.reinforcementsRemaining > 0) {
        return fail('Place all reinforcements before ending deploy');
      }
      return ok;
    case 'attack':
      if (state.turn.attack.kind !== 'free') {
        return fail('Cannot end attack while awaiting advance');
      }
      return ok;
    case 'fortify':
      return fail('Use endTurn from fortify phase');
  }
}

function validateEndTurn(state: GameState): ActionValidation {
  if (state.turn.phase !== 'fortify') {
    return fail('Can only end turn from fortify phase');
  }
  return ok;
}

/**
 * BFS through territories owned by ANYONE on `player`'s team, starting
 * at `from`. True iff `to` is reachable along team-territory edges.
 *
 * Source and destination ownership is checked separately by the
 * caller — this helper only walks the path. Allowing the path to
 * route through teammate-owned tiles is the core team-fortify rule;
 * units don't enter teammate space (source and dest must both belong
 * to the acting player), but the route can pass through it.
 */
function isConnectedViaTeam(
  state: GameState,
  map: MapDefinition,
  from: TerritoryId,
  to: TerritoryId,
  player: PlayerId
): boolean {
  const adj = new Map(map.territories.map((t) => [t.id, t.adjacent]));
  const visited = new Set<TerritoryId>([from]);
  const queue: TerritoryId[] = [from];
  while (queue.length > 0) {
    const here = queue.shift()!;
    const neighbors = adj.get(here) ?? [];
    for (const n of neighbors) {
      if (visited.has(n)) continue;
      const ts = state.territories[n];
      if (!ts) continue;
      if (!sameTeam(state, ts.owner, player)) continue;
      if (n === to) return true;
      visited.add(n);
      queue.push(n);
    }
  }
  return false;
}
